<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { sendCodeApi } from '@Api/otp.api';
import { validatePhoneNumberApi } from '@Api/phoneLookup.api';
import { useDeviceAuthStore } from '@Stores/deviceAuth.store';
import { useAuthSessionStore } from '@Stores/authSession.store';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import { getHighRiskCountries } from '@Src/mfe.config';
import { AuthContext } from '@Enums/authContext.enum';
import { MfaStatus } from '@Enums/mfaStatus.enum';
import type { OtpDeliveryType } from '@Types/otp.interface';

import { GuidedShowcaseLayout, ActionBarLayout } from '@Shared';
import ShowcaseImage from '@Components/ShowcaseImage.vue';

const { proxy } = getCurrentInstance();
const router = useRouter();
const route = useRoute();
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const deviceAuthStore = useDeviceAuthStore();
const authSessionStore = useAuthSessionStore();
const { loginHint } = storeToRefs(deviceAuthStore);

// Two error surfaces, kept separate so the two failure modes read differently:
//  - phoneFieldMessage → field-level validation error shown ON the phone input (case 1:
//    an empty / malformed / unreachable / Twilio-rejected number).
//  - formErrorMessage  → form-level red text at the bottom of the form (case 2: a delivery
//    failure or otherwise unknown send failure, plus any unhandled status).
const INVALID_PHONE_MESSAGE = 'Please enter a valid phone number.';
const EMPTY_PHONE_MESSAGE = 'Please enter your phone number.';
const SEND_FAILED_MESSAGE = 'Unable to send code. Please try again.';

const deliveryType = ref<OtpDeliveryType>('Sms');
const phoneNumber = ref<string>('');
const isSending = ref(false);
const phoneFieldMessage = ref<string | null>(null);
const formErrorMessage = ref<string | null>(null);
// Phone validity is driven by AppTelInput's @validity event (libphonenumber-based).
const phoneValidity = ref({ isValid: false, isEmpty: true });
const onValidity = (v: { isValid: boolean; isEmpty: boolean }) => {
    phoneValidity.value = v;
};

// Clear both error surfaces as the user edits the number so a stale send-time error doesn't
// linger while they correct it.
watch(phoneNumber, () => {
    phoneFieldMessage.value = null;
    formErrorMessage.value = null;
});

// Drives the red error state on the phone input whenever a field-level validation message
// is showing.
const phoneFieldStatus = computed(() => (phoneFieldMessage.value ? 'error' : 'default'));

// High-risk countries hidden from the picker, supplied by the host (from the host BFF
// app settings, backed by KeyVault). Set once at MFE init, before this view mounts.
// UX-only; server-side OTP enforcement in the auth BFF is independent. Empty => no exclusions.
const highRiskCountries = getHighRiskCountries();

const hasHint = computed(() => !!loginHint.value);
const isPaymentsContext = computed(
    () => (route.query.context?.toString() ?? '') === AuthContext.Payments,
);

onMounted(() => {
    if (!hasHint.value && !isPaymentsContext.value) {
        deviceAuthStore.setError('Unable to proceed with verification.');
        router.push({ name: 'general-error' });
    }
});

const sendCode = async () => {
    if (isSending.value) return;

    // Guard against the onMounted redirect race: the action bar is always rendered, so a
    // click on "Send Code" can arrive while the router.push({ name: 'general-error' })
    // above is still in flight. In that invalid state the non-Payments path would call
    // sendCodeApi with phoneNumber: undefined; short-circuit instead.
    if (!hasHint.value && !isPaymentsContext.value) return;

    // The intl picker yields canonical E.164.
    const normalizedPhone = phoneNumber.value;

    if (isPaymentsContext.value) {
        // Intl picker: validity is libphonenumber-based (E.164).
        if (!phoneValidity.value.isValid) {
            phoneFieldMessage.value = phoneValidity.value.isEmpty
                ? EMPTY_PHONE_MESSAGE
                : INVALID_PHONE_MESSAGE;
            return;
        }
    }

    isSending.value = true;
    phoneFieldMessage.value = null;
    formErrorMessage.value = null;

    try {
        // Payments manual entry: confirm the number can actually receive a code before
        // sending (mirrors the webapp's reachability gate). AppTelInput's v-model is E.164.
        // Non-Payments has no manual number (loginHint resolves it server-side), so only
        // guard the Payments path.
        if (isPaymentsContext.value && normalizedPhone) {
            const reachable = await validatePhoneNumberApi(normalizedPhone);
            if (!reachable) {
                // Case 1: the number can't receive a code — flag it on the input.
                phoneFieldMessage.value = INVALID_PHONE_MESSAGE;
                return;
            }
        }

        // In Payments we always send the canonical E.164 the user typed — exactly what the
        // reachability check validated. In non-Payments contexts we have a hint and the
        // redacted display (e.g., "(***) ***-1234") is not a real phone number — let the
        // BFF resolve the actual number from the activity session by omitting phoneNumber
        // from the payload.
        const payloadPhone = isPaymentsContext.value
            ? normalizedPhone
            : undefined;
        const response = await sendCodeApi({
            phoneNumber: payloadPhone,
            deliveryType: deliveryType.value,
        });

        deviceAuthStore.setMfaStatus(response.status);

        if (response.status === MfaStatus.Initialized) {
            deviceAuthStore.setLastDeliveryType(deliveryType.value);
            // Persist the typed phone number in Payments flow so the downstream
            // VerifyPasscode screen can resend / display it — there's no real
            // loginHint to fall back on in manual-entry.
            if (isPaymentsContext.value && normalizedPhone) {
                authSessionStore.setPhoneNumber(normalizedPhone);
            }
            proxy.$apptoast('An access code is being sent to your mobile device.');

            await router.push({ name: 'verify-passcode' });
            return;
        }

        // Case 1: the send layer rejected the number as invalid (Twilio InvalidPhoneNumber →
        // Locked). Only Payments has an editable input to flag; elsewhere fall through to the
        // form-level message below.
        if (response.status === MfaStatus.Locked && isPaymentsContext.value) {
            phoneFieldMessage.value = INVALID_PHONE_MESSAGE;
            return;
        }

        // Case 2: delivery failure. The BFF remaps a genuine SendFailed to Failed before it
        // reaches here, so on this screen we treat Failed as "couldn't deliver". In Payments
        // we keep the user on the screen to retry / change the number; in non-Payments the
        // number is server-resolved (nothing to edit), so route to the terminal failure page.
        if (response.status === MfaStatus.Failed) {
            if (isPaymentsContext.value) {
                formErrorMessage.value = SEND_FAILED_MESSAGE;
                return;
            }
            await router.push({ name: 'mfa-failed' });
            return;
        }

        // Any other unhandled status (Locked in non-Payments, Cooldown, TryAgain, …).
        formErrorMessage.value = SEND_FAILED_MESSAGE;
    } catch (err) {
        console.error('[Auth MFE] sendCodeApi failed:', err);
        // Best-effort: a 404 whose body flags the number as invalid is a case-1 signal. This is
        // rare — the number is normally caught client-side and by the reachability check first.
        const errorResponse = (err as {
            response?: { status?: number; data?: { message?: string } };
        })?.response;
        const isInvalidPhone404 =
            errorResponse?.status === 404 &&
            typeof errorResponse?.data?.message === 'string' &&
            errorResponse.data.message.toLowerCase().includes('invalid phone');
        if (isInvalidPhone404 && isPaymentsContext.value) {
            phoneFieldMessage.value = INVALID_PHONE_MESSAGE;
        } else {
            formErrorMessage.value = SEND_FAILED_MESSAGE;
        }
    } finally {
        isSending.value = false;
    }
};
</script>

<template>
    <guided-showcase-layout
        suppress-back-to-dashboard
        title="Verify your phone number"
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="['verify-phone', 'verify-code']"
        current-step="0">

        <app-form-content-layout>

            <p class="phone-entry__delivery-copy">We will send you a security code to the following phone number:</p>

            <div v-if="isPaymentsContext" class="phone-entry__input">
                <app-tel-input
                    v-model="phoneNumber"
                    label="Phone number"
                    default-country="US"
                    placeholder="(555) 555-5555"
                    :is-required="true"
                    data-id="phoneNumber"
                    :field-status="phoneFieldStatus"
                    :message="phoneFieldMessage"
                    :exclude-countries="highRiskCountries"
                    @validity="onValidity" />
            </div>
            <div
                v-else-if="hasHint"
                class="phone-entry__redacted-display"
                data-testid="redacted-phone"
                data-id="redactedPhoneNumber">
                {{ loginHint }}
            </div>

            <p bottomOffset="1rem">How do you want to receive the code?</p>
            <div class="phone-entry__toggle-group">

                <app-radio
                    v-model="deliveryType"
                    class="phone-entry__toggle-item"
                    radio-style="outlined"
                    native-value="Sms"
                    data-id="delivery-sms">
                        Text
                </app-radio>
                <app-radio
                    v-model="deliveryType"
                    class="phone-entry__toggle-item"
                    radio-style="outlined"
                    native-value="PhoneCall"
                    data-id="delivery-voice">
                        Call
                </app-radio>
            </div>

            <p v-if="formErrorMessage" class="phone-entry__error" role="alert" data-testid="send-error">
                {{ formErrorMessage }}
            </p>

        </app-form-content-layout>

        <action-bar-layout @back-click="goBackInFlow">
            <app-button
                expanded
                size="large"
                :is-loading="isSending"
                data-id="send-code"
                @onClick="sendCode">
                    Send Code
            </app-button>
        </action-bar-layout>

        <template #branded>
            <showcase-image />
        </template>

    </guided-showcase-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.phone-entry {
    &__redacted-display {
        background-color: $gray-light;
        border: 1px solid $dark-gray;
        border-radius: 0.375rem;
        padding: 0.875rem 1rem;
        color: $black-high;
        font-size: 1rem;
        min-height: 1.25rem;
        word-break: break-word;
    }

    &__delivery-copy {
        line-height: 1.5;
    }

    &__toggle-item {
        width: 100%;
    }

    &__toggle-group {
        display: flex;
        gap: 0.75rem;
    }

    &__error {
        color: $red;
        margin-top: 0.5rem;
    }

}
</style>
